import React, { useEffect, useState } from 'react';
import { Project } from '../../types/project';
import { Task } from '../../types/task';
import { NonProductiveCategory } from '../../types/non-productive-category';
import Card from '../Card';
import Button from '../Button';

interface QuickTimeEntryFormProps {
  entryDate: string;
  entryProjectId: string | null;
  entryCategoryId: string | null;
  entryTaskId: string | null;
  entryHours: number;
  entryDescription: string;
  entryBillable: boolean;
  projects: Project[];
  tasks: Task[];
  categories: NonProductiveCategory[];
  onDateChange: (date: string) => void;
  onProjectChange: (projectId: string) => void;
  onCategoryChange: (categoryId: string) => void;
  onTaskChange: (taskId: string | null) => void;
  onHoursChange: (hours: number) => void;
  onDescriptionChange: (description: string) => void;
  onBillableChange: (billable: boolean) => void;
  onSubmit: () => void;
  onCancel: () => void;
}

const MAX_HOURS_PER_DAY = 24;

const formatHoursDisplay = (hours: number): string => {
  if (!Number.isFinite(hours) || hours <= 0) {
    return '';
  }
  const fixed = hours.toFixed(2);
  const [whole, fraction] = fixed.split('.');
  return `${whole.padStart(2, '0')}.${fraction}`;
};

const sanitizeHoursInput = (inputValue: string): string => {
  const cleaned = inputValue.replace(/[^\d.]/g, '');
  const parts = cleaned.split('.');
  const whole = (parts[0] || '').slice(0, 2);
  const decimals = parts.slice(1).join('').slice(0, 2);
  if (cleaned.includes('.')) {
    return decimals ? `${whole}.${decimals}` : `${whole}.`;
  }
  return whole;
};

const clampHours = (value: number): number => {
  if (!Number.isFinite(value)) {
    return 0;
  }
  return Math.min(Math.max(value, 0), MAX_HOURS_PER_DAY);
};

const roundToQuarterHour = (value: number): number => {
  return Math.round(value / 0.25) * 0.25;
};

const QuickTimeEntryForm: React.FC<QuickTimeEntryFormProps> = ({
  entryDate,
  entryProjectId,
  entryCategoryId,
  entryTaskId,
  entryHours,
  entryDescription,
  entryBillable,
  projects,
  tasks,
  categories,
  onDateChange,
  onProjectChange,
  onCategoryChange,
  onTaskChange,
  onHoursChange,
  onDescriptionChange,
  onBillableChange,
  onSubmit,
  onCancel,
}) => {
  const [hoursInput, setHoursInput] = useState<string>(formatHoursDisplay(entryHours));
  const [entryType, setEntryType] = useState<'project' | 'category'>(
    entryCategoryId ? 'category' : 'project'
  );

  // Determine if billable checkbox should be locked
  const selectedCategory = categories.find(c => c.id === entryCategoryId);
  const isBillableLocked = selectedCategory?.is_system || false;

  useEffect(() => {
    setHoursInput(formatHoursDisplay(entryHours));
  }, [entryHours]);

  useEffect(() => {
    // Update entry type based on which field is set
    if (entryCategoryId) {
      setEntryType('category');
    } else if (entryProjectId) {
      setEntryType('project');
    }
  }, [entryProjectId, entryCategoryId]);

  const handleEntryTypeChange = (type: 'project' | 'category') => {
    setEntryType(type);
    // Clear the other field when switching types
    if (type === 'project') {
      onCategoryChange('');
    } else {
      onProjectChange('');
      onTaskChange(null);
    }
  };

  const handleCategoryChange = (categoryId: string) => {
    onCategoryChange(categoryId);
    // Force billable to false if system category
    const category = categories.find(c => c.id === categoryId);
    if (category?.is_system) {
      onBillableChange(false);
    }
  };

  const handleHoursInputChange = (value: string) => {
    const sanitized = sanitizeHoursInput(value);
    setHoursInput(sanitized);

    const parsed = parseFloat(sanitized);
    if (!Number.isNaN(parsed)) {
      onHoursChange(clampHours(parsed));
    } else {
      onHoursChange(0);
    }
  };

  const handleHoursInputBlur = () => {
    if (!hoursInput) {
      return;
    }

    const parsed = clampHours(parseFloat(hoursInput));
    const rounded = roundToQuarterHour(parsed);
    const formatted = formatHoursDisplay(rounded);
    setHoursInput(formatted);
    onHoursChange(rounded);
  };

  return (
    <Card variant="default" className="mb-6">
      <Card.Header title="Quick Time Entry" />
      <Card.Body>
        {/* Entry Type Tab Selector */}
        <div className="flex gap-2 mb-4 border-b-2 border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 rounded-t-md px-2">
          <button
            type="button"
            onClick={() => handleEntryTypeChange('project')}
            className={`px-4 py-2 font-medium text-sm cursor-pointer transition-all duration-200 rounded-t-md -mb-0.5 ${
              entryType === 'project'
                ? 'bg-white dark:bg-gray-900 border-b-2 border-primary-600 text-primary-600 dark:text-primary-400 font-bold'
                : 'bg-transparent hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400 border-b-2 border-transparent'
            }`}
          >
            📊 Project Time
          </button>
          <button
            type="button"
            onClick={() => handleEntryTypeChange('category')}
            className={`px-4 py-2 font-medium text-sm cursor-pointer transition-all duration-200 rounded-t-md -mb-0.5 ${
              entryType === 'category'
                ? 'bg-white dark:bg-gray-900 border-b-2 border-accent-600 text-accent-600 dark:text-accent-400 font-bold'
                : 'bg-transparent hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400 border-b-2 border-transparent'
            }`}
          >
            📋 Non-Productive Time
          </button>
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Date</label>
            <input
              type="date"
              className="w-full p-2.5 text-sm text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md transition-all focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
              value={entryDate}
              onChange={(e) => onDateChange(e.target.value)}
              max={new Date().toISOString().split('T')[0]}
              required
            />
          </div>

          {entryType === 'project' ? (
            <>
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Project *</label>
                <select
                  className="w-full p-2.5 text-sm text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md transition-all focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                  value={entryProjectId || ''}
                  onChange={(e) => onProjectChange(e.target.value)}
                  required
                >
                  <option value="">Select project...</option>
                  {projects.map(project => (
                    <option key={project.id} value={project.id}>
                      {project.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Task (optional)</label>
                <select
                  className="w-full p-2.5 text-sm text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md transition-all focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                  value={entryTaskId || ''}
                  onChange={(e) => onTaskChange(e.target.value ? e.target.value : null)}
                  disabled={!entryProjectId}
                >
                  <option value="">No specific task</option>
                  {tasks
                    .filter(t => t.project_id === entryProjectId)
                    .map(task => (
                      <option key={task.id} value={task.id}>
                        {task.title}
                      </option>
                    ))}
                </select>
              </div>
            </>
          ) : (
            <>
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Category *</label>
                <select
                  className="w-full p-2.5 text-sm text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md transition-all focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                  value={entryCategoryId || ''}
                  onChange={(e) => handleCategoryChange(e.target.value)}
                  required
                >
                  <option value="">Select category...</option>
                  <optgroup label="System Categories">
                    {categories.filter(c => c.is_system).map(category => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </optgroup>
                  {categories.filter(c => !c.is_system).length > 0 && (
                    <optgroup label="Custom Categories">
                      {categories.filter(c => !c.is_system).map(category => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </optgroup>
                  )}
                </select>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Category Info</label>
                <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-md text-sm text-gray-600 dark:text-gray-400">
                  {selectedCategory ? selectedCategory.description || 'No description' : 'Select a category'}
                </div>
              </div>
            </>
          )}
        </div>

        <div className="grid grid-cols-[100px_1fr_auto] gap-4 mt-4">
          <div className="mb-6 m-0">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Hours *</label>
            <input
              type="text"
              className="w-full p-2.5 text-sm text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md transition-all focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
              inputMode="decimal"
              placeholder="00.00"
              value={hoursInput}
              onChange={(e) => handleHoursInputChange(e.target.value)}
              onBlur={handleHoursInputBlur}
              required
            />
          </div>

          <div className="mb-6 m-0">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Description</label>
            <input
              type="text"
              className="w-full p-2.5 text-sm text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md transition-all focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
              value={entryDescription}
              onChange={(e) => onDescriptionChange(e.target.value)}
              placeholder="What did you work on?"
            />
          </div>

          <div className="mb-6 m-0">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Billable?</label>
            {isBillableLocked ? (
              <div className="flex items-center h-10.5 px-2 bg-warning/10 dark:bg-warning/20 border border-warning/30 text-warning rounded-md text-sm">
                <span className="text-warning">
                  <strong>No</strong> (system category)
                </span>
              </div>
            ) : (
              <div className="flex items-center h-10.5">
                <input
                  type="checkbox"
                  checked={entryBillable}
                  onChange={(e) => onBillableChange(e.target.checked)}
                  className="w-5 h-5"
                />
              </div>
            )}
          </div>
        </div>

        {isBillableLocked && (
          <div className="mt-2 p-2 bg-warning/10 dark:bg-warning/20 border border-warning/30 rounded-md text-sm text-warning">
            ⚠️ System categories (Leave, Meetings, Admin, Training) are always non-billable
          </div>
        )}
      </Card.Body>
      <Card.Footer>
        <Button onClick={onCancel} variant="outline">
          Cancel
        </Button>
        <Button onClick={onSubmit} variant="primary">
          Log Time
        </Button>
      </Card.Footer>
    </Card>
  );
};

export default QuickTimeEntryForm;
